import React, { useMemo } from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AvenyCollectionMap } from '../../../../../../../../Model/ModelContext/ModelContext';
import { DISABLE_ALL_BUTTON_TOOLTIP } from '../EkonomiskAnalysSideMenu';
import { ISumCategory } from '../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { ISelection } from '../../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import { IUserInfo } from '../../../Utils/ekonomiskAnalysInitQueryUtils';
import {
  ISelectedAnalys,
  ISelectedAnalysAnalys,
  ISelectedAnalysCodePrivilege,
  IGrundurval,
} from '../../../EkonomiskAnalysPage';
import {
  SidePanels,
  IFilledLocalAnalys,
  ILocalAnalys,
} from '../../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  getNewAnalysColumns,
  IColumnTemplate,
} from '../Components/PeriodOchKolumner/Queries/AvailableColumnTemplatesHandler';
import EDList from '../../../../../../../../BaseComponents/List/EDList';
import EkonomiskAnalysAnalyseraListItem from '../Components/Analysera/EkonomiskAnalysAnalyseraListItem';
import EkonomiskAnalysCreateNewListItem from '../Components/CreateNewAnalys/EkonomiskAnalysCreateNewListItem';
import EkonomiskAnalysSelect from '../EkonomiskAnalysSelect/EkonomiskAnalysSelect';
import EkonomiskAnalysExpandableListItem from '../Components/ListItems/EkonomiskAnalysExpandableListItem';
import EkonomiskAnalysUrvalSummering from '../Components/UrvalOchSummering/EkonomiskAnalysUrvalSummering';
import EkonomiskAnalysPeriodKolumner from '../Components/PeriodOchKolumner/EkonomiskAnalysPeriodKolumner';
import EkonomiskAnalysOther from '../Components/Other/EkonomiskAnalysOther';

interface IProps {
  availableCategories: ISumCategory[];
  availableColumnTemplates: IColumnTemplate[];
  sideMenuIsOpen: boolean;
  openSideMenu: (panel?: SidePanels, callback?: () => void) => () => void;
  onAnalysSelected: (
    analys: ISelectedAnalysAnalys,
    codePrivilege: ISelectedAnalysCodePrivilege,
    sammanfattningsMode: boolean,
    newlyCreatedAnalys?: boolean
  ) => void;
  handleExpansion: (panel: SidePanels, callback?: () => void) => () => void;
  helaHuvudboken: IGrundurval | undefined;
  selectedAnalys: ISelectedAnalys | null;
  columnsExpanded: boolean;
  activeDatabaseName: string;
  startAnalysId?: string | null;
  newAnalysSettings: ILocalAnalys;
  currentAnalysSettings: IFilledLocalAnalys;
  urvalOchSummeringExpanded: boolean;
  otherExpanded: boolean;
  summeringChanged: boolean;
  urvalChanged: boolean;
  ovrigaValChanged: boolean;
  periodKolumnerChanged: boolean;
  settingsChangedFromOriginal: boolean;
  collectionMap: AvenyCollectionMap;
  availableReports: ISelection[];
  unsavedAnalys: boolean;
  createNewModalOpen: boolean;
  openCreateNewModal: () => void;
  closeCreateNewModal: () => void;
  isLopandeAnalys: boolean;
  userInfo?: IUserInfo;
  updateReport: () => void;
  disableAll?: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  toggleOpenColumns: () => void;
  openSideMenuColumns: () => void;
  analysSelectOpen: boolean;
  setAnalysSelectOpen: (newValue: boolean) => void;
  sammanfattningsMode: boolean;
  isInDetaljanalysTab: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  listContainer: {
    flex: '0 1 100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
}));

const EkonomiskAnalysSideMenuList: React.FC<
  React.PropsWithChildren<IProps>
> = ({ scrollContainerRef, ...props }) => {
  const { classes } = useStyles();

  const disabledReason = useMemo(() => {
    if (props.disableAll) {
      return DISABLE_ALL_BUTTON_TOOLTIP;
    }

    if (props.sammanfattningsMode) {
      return 'Redigering kan inte göras i rapportkategori Detaljanalyser';
    }

    return undefined;
  }, [props.disableAll, props.sammanfattningsMode]);

  const disabledReasonCreateNew = useMemo(() => {
    if (props.disableAll) {
      return DISABLE_ALL_BUTTON_TOOLTIP;
    } else if (props.isInDetaljanalysTab) {
      return 'Ny analys kan inte skapas när du befinner dig i Detaljanalysen';
    }

    return undefined;
  }, [props.disableAll, props.isInDetaljanalysTab]);

  return (
    <div className={classes.listContainer} ref={scrollContainerRef}>
      <EDList disableVerticalPadding>
        <EkonomiskAnalysAnalyseraListItem
          sideMenuIsOpen={props.sideMenuIsOpen}
          updateReport={props.updateReport}
          haveSelectedAnalys={props.selectedAnalys != null}
          disabled={props.disableAll}
          disabledReason={
            props.disableAll ? DISABLE_ALL_BUTTON_TOOLTIP : undefined
          }
        />
        <EkonomiskAnalysCreateNewListItem
          sideMenuIsOpen={props.sideMenuIsOpen}
          availableReports={props.availableReports}
          availableCategories={props.availableCategories}
          dates={props.currentAnalysSettings.dates}
          grundurval={props.helaHuvudboken}
          dialogIsOpen={props.createNewModalOpen}
          openDialog={props.openCreateNewModal}
          closeDialog={props.closeCreateNewModal}
          newAnalysColumns={getNewAnalysColumns(props.availableColumnTemplates)}
          disabled={disabledReasonCreateNew !== undefined}
          disabledReason={disabledReasonCreateNew}
        />
        <EkonomiskAnalysSelect
          database={props.activeDatabaseName}
          onSelected={props.onAnalysSelected}
          selectedAnalys={props.selectedAnalys}
          startAnalysId={props.startAnalysId}
          sideMenuIsOpen={props.sideMenuIsOpen}
          openSideMenu={props.openSideMenu(SidePanels.select)}
          analysSelectOpen={props.analysSelectOpen && props.sideMenuIsOpen}
          setAnalysSelectOpen={props.setAnalysSelectOpen}
          analysGroupingMode={
            props.userInfo ? props.userInfo.analysGroupingMode : undefined
          }
          disabled={props.disableAll}
          disabledReason={
            props.disableAll ? DISABLE_ALL_BUTTON_TOOLTIP : undefined
          }
          sammanfattningsMode={props.sammanfattningsMode}
        />
        {props.selectedAnalys && (
          <>
            <EkonomiskAnalysExpandableListItem
              text="Summering och urval"
              iconType="table_row"
              open={props.urvalOchSummeringExpanded}
              toggleOpen={props.handleExpansion(SidePanels.urval)}
              sideMenuOpen={props.sideMenuIsOpen}
              openSideMenu={props.openSideMenu(SidePanels.urval)}
              changed={props.summeringChanged || props.urvalChanged}
              disabled={disabledReason !== undefined}
              disabledReason={disabledReason}
            >
              <EkonomiskAnalysUrvalSummering
                summeringChanged={props.summeringChanged}
                urvalChanged={props.urvalChanged}
                isLopandeAnalys={props.isLopandeAnalys}
                sideMenuOpen={props.sideMenuIsOpen}
                collectionMap={props.collectionMap}
                updateReport={props.updateReport}
              />
            </EkonomiskAnalysExpandableListItem>
            <EkonomiskAnalysExpandableListItem
              text="Period och kolumner"
              iconType="table_chart"
              open={props.columnsExpanded}
              toggleOpen={props.toggleOpenColumns}
              sideMenuOpen={props.sideMenuIsOpen}
              openSideMenu={props.openSideMenuColumns}
              changed={props.periodKolumnerChanged}
              unmountOnExit
              disabled={disabledReason !== undefined}
              disabledReason={disabledReason}
            >
              <EkonomiskAnalysPeriodKolumner
                availableColumnTemplates={props.availableColumnTemplates}
                selectedAnalys={props.selectedAnalys}
              />
            </EkonomiskAnalysExpandableListItem>
            <EkonomiskAnalysExpandableListItem
              text="Övriga val"
              iconType="table_settings"
              open={props.otherExpanded}
              toggleOpen={props.handleExpansion(SidePanels.other)}
              sideMenuOpen={props.sideMenuIsOpen}
              openSideMenu={props.openSideMenu(SidePanels.other)}
              changed={props.ovrigaValChanged}
              disabled={disabledReason !== undefined}
              disabledReason={disabledReason}
            >
              <EkonomiskAnalysOther
                activeDatabaseName={props.activeDatabaseName}
              />
            </EkonomiskAnalysExpandableListItem>
          </>
        )}
      </EDList>
    </div>
  );
};

export default EkonomiskAnalysSideMenuList;
